import React, { useEffect, useRef, useState } from "react"
import { Typography, Box, Button } from "@mui/material"
import Skeleton from "react-loading-skeleton"
import Api from "../../../../_helpers/Api"
import { trans } from "../../../../_providers/Translation"
import { LineChart } from "@mui/x-charts"
import filterImage from "@/assets/img/icons/filter.svg"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DatePicker from "@/components/partials/Date"

const INITIAL_FILTERS = { partner: "", status: null }

function PartsChart({ title, apiEndpoint }) {
  const [loading, setLoading] = useState(true)
  const [chartData, setChartData] = useState([])
  const [days, setDays] = useState([])
  const [partners, setPartners] = useState([])
  const [statuses, setStatuses] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)

  useEffect(() => {
    setLoading(true)

    const endpointWithFilterParams = `${apiEndpoint}?partner_id=${filters.partner}&status_id=${filters.status || ""}`

    Api.get(endpointWithFilterParams)
      .then((res) => {
        if (res.data) {
          setChartData(res.data.data.chartData || [])
          setDays(res.data.data.days || [])
          setPartners(res.data.data.partners || [])
          setStatuses(res.data.data.statuses || [])
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [filters])

  const series = chartData.map((item, index) => ({
    curve: "linear",
    data: item.data,
    area: true,
    color: item.color,
    label: item.name,
    showMark: true,
    id: `series-${index}`,
  }))

  function getIfAnyData() {
    return chartData.some((item) => item.data.some((point) => point > 0))
  }

  function onFilterChange(e) {
    const { name, value } = e.target

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.partner) count++
    if (filters.status) count++
    return count
  }

  return (
    <div className="pannel">
      <div className="customers-chart">
        <div className="head">
          <h3>{title}</h3>
          <div className="right">
            <select
              onChange={onFilterChange}
              name="partner"
              value={filters.partner}
            >
              <option value="">{trans("random.allPartners")}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>

            <Button
              onClick={(e) => {
                compactFilterLayoutRef.current.open(e)
              }}
            >
              <img src={filterImage} alt="Filter" />
              {getAppliedFiltersCount() ? (
                <i>{getAppliedFiltersCount()}</i>
              ) : (
                <></>
              )}
            </Button>
            <CompactFilterLayout
              ref={compactFilterLayoutRef}
              appliedFiltersCount={getAppliedFiltersCount()}
              onClearClick={() => setFilters(INITIAL_FILTERS)}
            >
              <div className="row">
                <div className="col">
                  <select
                    onChange={onFilterChange}
                    name="status"
                    value={filters.status}
                  >
                    <option value="">{trans("filters.allStatuses")}</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CompactFilterLayout>
          </div>
        </div>
        <hr />
        {loading ? (
          <Skeleton height={220} count={1} />
        ) : !getIfAnyData() ? (
          <div
            className="alert warning"
            style={{
              width: "100%",
              marginBottom: "auto",
            }}
          >
            {trans("messages.noDataFound")}
          </div>
        ) : (
          <LineChart
            xAxis={[
              {
                data: days,
                scaleType: "point",
              },
            ]}
            series={series}
            height={287}
            grid={{ horizontal: true }}
            sx={{
              ...Object.fromEntries(
                chartData.map((item, index) => [
                  `& .MuiAreaElement-series-series-${index}`,
                  { fill: `url(#parts-gradient-${index})` },
                ]),
              ),
              "& .MuiLineElement-root": {
                strokeWidth: 2,
              },
              "& .MuiMarkElement-root": {
                stroke: "#fff",
                strokeWidth: 1,
                r: 5,
              },
              "& .MuiChartsGrid-line": {
                stroke: "#e0e0e0",
                strokeDasharray: "4 2",
              },
            }}
            slotProps={{
              legend: { hidden: true },
            }}
          >
            <defs>
              {chartData.map((item, index) => (
                <linearGradient
                  key={index}
                  id={`parts-gradient-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={item.color} stopOpacity={0.25} />
                  <stop
                    offset="100%"
                    stopColor={item.color}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              ))}
            </defs>
          </LineChart>
        )}
      </div>
    </div>
  )
}

export default PartsChart
